import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { createClient } from '@/lib/supabase/client';

export interface LeadScoreResult {
  urgency_score: number;
  budget_fit: number;
  property_size: 'small' | 'medium' | 'large';
  ai_summary: string;
  recommended_action: string;
}

interface LeadData {
  name: string;
  phone: string;
  serviceType: string;
  bedrooms: string;
  message: string;
}

export async function scoreLeadWithClaude(lead: LeadData): Promise<LeadScoreResult | null> {
  try {
    const prompt = `You are an expert property management lead analyst. Analyze this contact form submission and return a JSON object with exactly these fields:

- urgency_score (integer 1-10): How urgently does this lead need service? 10 = immediate need, 1 = just browsing
- budget_fit (integer 1-10): How well does this lead fit a premium property management service? 10 = perfect fit, 1 = poor fit
- property_size (string): Must be exactly one of: "small", "medium", or "large"
  - small = studio/1BR or properties under 1000 sqft
  - medium = 2-3BR or typical vacation rental
  - large = 4BR+ or multi-unit/luxury property
- ai_summary (string): One concise sentence (max 15 words) summarizing this lead's key opportunity
- recommended_action (string): One specific action for the sales team (max 10 words)

Lead Details:
- Name: ${lead.name}
- Phone: ${lead.phone}
- Service Requested: ${lead.serviceType}
- Bedrooms: ${lead.bedrooms || 'Not specified'}
- Message: ${lead.message || 'No message provided'}

Respond ONLY with valid JSON. No markdown, no explanation, just the JSON object.`;

    const response = await getChatCompletion(
      'ANTHROPIC',
      'claude-haiku-4-5',
      [
        { role: 'user', content: prompt }
      ],
      {
        temperature: 0.2,
        max_tokens: 300,
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content) return null;

    // Strip any markdown code fences if present
    const cleaned = content.replace(/```json\n?|```\n?/g, '').trim();
    const parsed = JSON.parse(cleaned) as LeadScoreResult;

    // Validate required fields
    if (
      typeof parsed.urgency_score !== 'number' ||
      typeof parsed.budget_fit !== 'number' ||
      !['small', 'medium', 'large'].includes(parsed.property_size) ||
      typeof parsed.ai_summary !== 'string' ||
      typeof parsed.recommended_action !== 'string'
    ) {
      console.warn('[LeadScoring] Invalid score structure:', parsed);
      return null;
    }

    // Clamp scores to 1-10
    parsed.urgency_score = Math.min(10, Math.max(1, Math.round(parsed.urgency_score)));
    parsed.budget_fit = Math.min(10, Math.max(1, Math.round(parsed.budget_fit)));

    return parsed;
  } catch (err) {
    console.warn('[LeadScoring] Claude scoring failed:', err);
    return null;
  }
}

export async function saveLeadScore(leadId: string, score: LeadScoreResult): Promise<void> {
  try {
    const supabase = createClient();
    const { error } = await supabase.from('lead_scores').insert({
      lead_id: leadId,
      urgency_score: score.urgency_score,
      budget_fit: score.budget_fit,
      property_size: score.property_size,
      ai_summary: score.ai_summary,
      recommended_action: score.recommended_action,
    });
    if (error) {
      console.warn('[LeadScoring] Failed to save score:', error.message);
    }
  } catch (err) {
    console.warn('[LeadScoring] Save score error:', err);
  }
}
